import type { ActivityOverviewDimension } from '@sparkapp/shared-types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { BreakdownBarList } from './BreakdownBarList';

interface DimensionBreakdownProps {
  title: string; // "By Pack", "By Play Type"
  note: string;
  rows: ActivityOverviewDimension[];
  missingCount: number; // health.noPack / health.noPlayType
  totalActivities: number;
  missingLabel: string; // "not in any pack"
  hrefFor?: (id: number) => string;
}

/**
 * Activity-level dimension (pack / play type): bar per item in canonical order,
 * plus a coverage line for activities with no tag at all.
 */
export function DimensionBreakdown({
  title,
  note,
  rows,
  missingCount,
  totalActivities,
  missingLabel,
  hrefFor,
}: DimensionBreakdownProps) {
  return (
    <Card className="bg-pine-card border-pine-line shadow-pine-card rounded-pine-card">
      <CardHeader>
        <CardTitle className="text-heading-4 text-pine-ink">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-caption text-pine-ink-soft">{note}</p>
        <p className={cn('text-sm', missingCount > 0 ? 'text-amber-800 font-medium' : 'text-pine-ink-soft')}>
          {totalActivities - missingCount} of {totalActivities} activities have at least one
          {missingCount > 0 && ` · ${missingCount} ${missingLabel}`}
        </p>
        {rows.length === 0 ? (
          <p className="text-sm text-pine-ink-soft">None configured.</p>
        ) : (
          <BreakdownBarList
            rows={rows.map((row) => ({
              key: String(row.id),
              label: <span className="truncate">{row.title}</span>,
              primary: row.activityCount,
              href: hrefFor?.(row.id),
            }))}
          />
        )}
      </CardContent>
    </Card>
  );
}
